import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import StatusMessage from "../../../components/common/StatusMessage";
import { Button } from "../../../components/ui";
import {
  InspectorSignatureSection,
  InspectorSubmitModal,
} from "../../../features/operations/tmlog/inspector";
import {
  getInspectorByToken,
  submitInspectorSignature,
} from "../../../services/api/tmlog";
import { ENV_CONFIG } from "../../../config/apiConfig";

/**
 * TMInspector Page - Public Inspector Review and Signature
 *
 * This is a PUBLIC page (no authentication required).
 * Accessed via unique URL: /inspector/{inspector_token}
 *
 * Features:
 * - Displays T&M report snapshot from when "Send to Inspector" was clicked
 * - Shows PDF viewer using unique pdf_uuid
 * - Allows inspector to add comments and signature
 * - One-time access only (checks accessed_at timestamp)
 * - Locks TMReport and deletes temp PDF after submission
 */
const TMInspector = () => {
  const { token } = useParams();
  const [inspector, setInspector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [comments, setComments] = useState("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    document.title = "Inspector Review - R&S";
  }, []);

  // Fetch inspector data on mount
  useEffect(() => {
    const fetchInspectorData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getInspectorByToken(token);
        setInspector(data);

        // Check if already accessed (one-time use only)
        if (data.accessed_at && data.status !== "signed") {
          setError(
            "This inspection link has already been accessed and is no longer available. " +
              "If you need to review this report again, please contact the project manager."
          );
        }

        // Check if already signed
        if (data.status === "signed") {
          setSubmitSuccess(true);
        }
      } catch (err) {
        console.error("Error fetching inspector data:", err);
        setError(
          err.message ||
            "Unable to load inspection data. This link may be invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInspectorData();
    } else {
      setError("Invalid inspection link - no token provided.");
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await submitInspectorSignature({
        inspector_token: token,
        inspector_signature: signature,
        inspector_comments: comments,
      });

      setSubmitSuccess(true);
      setShowSubmitModal(false);
    } catch (err) {
      console.error("Error submitting signature:", err);
      setError(
        err.message ||
          "Failed to submit signature. Please try again or contact the project manager."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignatureChange = (dataURL) => {
    setSignature(dataURL);
  };

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
  };

  const handleSubmitClick = () => {
    if (!signature) {
      setError("Please provide your signature before submitting.");
      return;
    }
    setShowSubmitModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid link, already accessed, etc.)
  if (error && !inspector) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6">
        <StatusMessage type="error" message={error} />
        <div className="mt-6 text-center text-gray-600">
          <p>If you believe this is an error, please contact:</p>
          <p className="font-semibold mt-2">info@rscminc.com</p>
        </div>
      </div>
    );
  }

  // Success state (signature submitted)
  if (submitSuccess || (inspector && inspector.inspector_signature)) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6">
        <div className="bg-green-50 border border-green-300 rounded-lg p-6 text-center">
          <div className="text-green-600 text-5xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">
            Signature Submitted Successfully
          </h2>
          <p className="text-green-700 mb-4">
            Thank you for reviewing and signing the T&M report.
          </p>
          <p className="text-sm text-gray-600">
            The project manager has been notified. This form is now locked and
            can no longer be accessed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4 max-w-6xl ml-0 mx-[2px] md:mx-auto">
      <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-6 mb-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          T&M Report Inspector Review
        </h1>
        <p className="text-gray-600">
          Please review the report details and PDF below. Once reviewed, add any
          comments and provide your signature to approve.
        </p>
        <div className="mt-4 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded p-3">
          <strong>⚠️ Important:</strong> This a temporary link. Once you sign
          this report, you will receive a copy in your email.
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4">
          <StatusMessage type="error" message={error} />
        </div>
      )}

      {/* Report View */}
      {inspector && (
        <>
          {/* PDF Viewer */}
          <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">
              T&M Report PDF
            </h2>
            <div className="bg-gray-100 border border-gray-300 rounded overflow-hidden">
              <iframe
                src={`${ENV_CONFIG.API_URL}/tmlog/inspector/pdf/${inspector.pdf_uuid}/#toolbar=0`}
                title="T&M Report PDF"
                className="w-full"
                style={{ border: "none", aspectRatio: "8.5 / 11" }}
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              You can also{" "}
              <a
                href={`${ENV_CONFIG.API_URL}/tmlog/inspector/pdf/${inspector.pdf_uuid}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                open the PDF in a new tab
              </a>
              .
            </p>
          </div>

          {/* Signature Section */}
          <InspectorSignatureSection
            signature={signature}
            comments={comments}
            onSignatureChange={handleSignatureChange}
            onCommentsChange={handleCommentsChange}
          />

          {/* Submit Button */}
          <div className="mt-6 flex justify-center">
            <Button
              variant="primary"
              text="Submit Signature"
              onClick={handleSubmitClick}
              disabled={!signature || isSubmitting}
              className="px-8 py-3 text-lg"
            />
          </div>

          {/* Submit Confirmation Modal */}
          <InspectorSubmitModal
            isOpen={showSubmitModal}
            onClose={() => setShowSubmitModal(false)}
            onConfirm={handleSubmit}
            isSubmitting={isSubmitting}
            inspector={inspector}
          />
        </>
      )}
    </div>
  );
};

export default TMInspector;
